
import { DecimalPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControl, FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatLabel } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { BullhornDataSourceApiService } from 'datasource-service-lib';
import { FeatureFlagDirective, FeatureFlagService } from 'feature-flags-lib';
import { jwtDecode } from 'jwt-decode';
import { MessageNotificationService } from 'message-notification-service-lib';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../../environments/environment';
import { ConfigService } from '../../../services/config.service';
import { IKyloeCallIntegrationConfig, IMaskedGlobalNotetakerConfig, NoteTakerIntegrationApiService } from '../../../services/note-taker-integration-api.service';
import { I8x8MaskedConfig, NoteTaker8x8ApiService } from '../../../services/notetaker-8x8-api.service';
import { NotetakerCallsService } from '../../../services/notetaker-calls.service';
import { EightByEightSettingsComponent } from './eight-by-eight-settings/eight-by-eight-settings.component';
import { KyloeCallSettingsComponent } from './kyloe-call-settings/kyloe-call-settings.component';
import { RingoverSettingsComponent } from './ringover-settings/ringover-settings.component';

@Component({
  selector: 'app-note-taker-global-settings',
  imports: [
    MatDialogTitle,
    MatDialogActions,
    MatIcon,
    MatTab,
    MatTabGroup,
    FormsModule,
    ReactiveFormsModule,
    EightByEightSettingsComponent,
    KyloeCallSettingsComponent,
    MatExpansionModule,
    MatTooltip,
    MatDialogContent,
    RingoverSettingsComponent,
    MatSlideToggle,
    MatLabel,
    FeatureFlagDirective,
    DecimalPipe,
  ],
  templateUrl: './note-taker-global-settings.component.html',
  styleUrl: './note-taker-global-settings.component.scss',
})
export class NoteTakerGlobalSettingsComponent implements OnInit {
  protected readonly oneNotePerCallFlagKey = FeatureFlagService.FLAGS.oneNotePerCall;
  protected readonly customSummaryPromptFlagKey = FeatureFlagService.FLAGS.customSummaryPrompt;
  protected readonly summaryPromptMaxLength = 15000; // kept below Bedrock's 20,000-character query limit

  public savedNoteTakerGlobalConfig = new BehaviorSubject<IMaskedGlobalNotetakerConfig | null>(null);
  public saved8x8Config = new BehaviorSubject<I8x8MaskedConfig | null>(null);

  private validDomains$: Observable<string[]>;
  private corporationId: string;

  form = this.fb.group({
    kyloeCall: this.fb.group(
      {
        domains: this.fb.array([this.createDomainControl()]),
        botName: null,
        active: false,
      },
      { validators: [NoteTakerGlobalSettingsComponent.noneOrAllValidator] }
    ),
    ringover: this.fb.group({
      apiKey: null as string,
      webhookKey: null as string,
      enabled: false,
      tenantId: null as string,
    }),
    eightByEight: this.fb.group({
      apiKey: null as string,
      apiSecret: null as string,
      enabled: false,
    }),
    saveTranscriptAsFile: new FormControl(true),
    oneNotePerCall: new FormControl(false),
    summaryPrompt: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(this.summaryPromptMaxLength)],
    }),
  });

  private corpId: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<NoteTakerGlobalSettingsComponent>,
    private noteTakerIntegrationApiService: NoteTakerIntegrationApiService,
    private noteTaker8x8ApiService: NoteTaker8x8ApiService,
    private clientConfigService: ConfigService,
    private notetakerCallsService: NotetakerCallsService,
    private identityStorage: IdentityStorage,
    private messageNotificationService: MessageNotificationService,
    private bullhornDataSource: BullhornDataSourceApiService,
  ) {
    this.corpId = this.data?.namespaceId;
    const token = this.identityStorage.get()?.token;
    const decodedToken = jwtDecode<any>(token);
    this.corporationId = decodedToken?.corporationId;
    this.validDomains$ = this.bullhornDataSource.getValidDomainsForCorporation(this.corporationId).pipe(shareReplay(1));
  }

  static noneOrAllValidator(control: FormGroup): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const entries = Object.entries(control.value).filter(
      ([key, _value]) => !((key === 'enabled' || key === 'active') && _value === false),
    );
    const filledEntries = entries.filter(([_key, value]) => {
      if (Array.isArray(value)) {
        return value.some(v => v && v.toString().trim() !== '');
      }
      return value && value.toString().trim() !== '';
    });
    return filledEntries.length === 0 || filledEntries.length === entries.length
      ? null
      : { noneOrAll: true };
  }

  static allValuesOrNull(value: any): any {
    if (!value) {
      return null;
    }
    const entries = Object.entries(value).filter(([key, _value]) => key !== 'enabled');
    const filledEntries = entries.filter(
      ([key, _value]) => _value && _value.toString().trim() !== ''
    );
    return filledEntries.length === entries.length ? value : null;
  }

  createDomainControl(): FormControl {
    return new FormControl(null, {
      validators: [this.validateDomainFormat()],
      asyncValidators: [this.validateDomainAgainstBullhorn()],
    });
  }

  get domainsArray(): FormArray {
    return this.form.get('kyloeCall.domains') as FormArray;
  }

  get kyloeCallErrorMessage(): string {
    const domainsArray = this.domainsArray;
    const hasBullhornError = domainsArray.controls.some(
      control => control.errors?.['notInBullhorn']
    );

    if (hasBullhornError) {
      return 'Email domains do not match what is allowed in your Bullhorn corporation';
    }

    return 'Make sure that all fields are filled out';
  }

  addDomain(): void {
    this.domainsArray.push(this.createDomainControl());
  }

  removeDomain(index: number): void {
    if (this.domainsArray.length > 1) {
      this.domainsArray.removeAt(index);
    }
  }

  validateDomainFormat(): ValidatorFn {
    const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || control.value === '') {
        return null;
      }
      return domainRegex.test(control.value) ? null : { invalidFormat: true };
    };
  }

  validateDomainAgainstBullhorn(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || control.value === '') {
        return of(null);
      }

      return of(control.value).pipe(
        debounceTime(500),
        switchMap(() => this.validDomains$),
        take(1),
        map((validDomains: string[]) => {
          const domain = control.value.toLowerCase();
          return validDomains.includes(domain) ? null : { notInBullhorn: true };
        }),
        catchError(() => of(null))
      );
    };
  }

  ngOnInit(): void {
    this.loadIntegrationConfig();
    this.load8x8Config();
    this.loadCallOptions();
  }

  private loadIntegrationConfig(): void {
    this.noteTakerIntegrationApiService.getIntegrationConfig().subscribe((config) => {
      this.savedNoteTakerGlobalConfig.next(config);

      const domains = config.kyloeCall?.domains || [];

      this.domainsArray.clear();
      if (domains.length > 0) {
        domains.forEach(domain => {
          const control = this.createDomainControl();
          control.setValue(domain);
          this.domainsArray.push(control);
        });
      } else {
        this.domainsArray.push(this.createDomainControl());
      }

      this.form.patchValue({
        kyloeCall: {
          botName: config.kyloeCall?.botName,
          active: config.kyloeCall?.active || false,
        },
        ringover: {
          ...config.ringover,
        },
      });
    });
  }

  private load8x8Config(): void {
    this.noteTaker8x8ApiService
      .getIntegrationConfig()
      .pipe(catchError(() => of(null)))
      .subscribe((config) => {
        this.saved8x8Config.next(config);
        if (config) {
          this.form.patchValue({
            eightByEight: {
              enabled: config.enabled ?? false,
            },
          });
        }
      });
  }

  private loadCallOptions(): void {
    if (!this.corpId) {
      return;
    }
    this.clientConfigService
      .getConfigForCorpId(environment.ntApiGatewayUrl, this.corpId)
      .pipe(
        catchError(() => {
          return [];
        }),
      )
      .subscribe((config) => {
        if (config) {
          this.form.patchValue({
            saveTranscriptAsFile: config.saveTranscriptAsFile ?? true,
            oneNotePerCall: config.oneNotePerCall ?? false,
            summaryPrompt: config.summaryPrompt ?? '',
          });
        }
      });
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    const ringoverValues = {
      ...this.form.value.ringover,
      webhookKey:
        this.form.value.ringover.webhookKey !== '' ? this.form.value.ringover.webhookKey : null,
      apiKey: this.form.value.ringover.apiKey !== '' ? this.form.value.ringover.apiKey : null,
    };

    const kyloeCallValue = this.form.value.kyloeCall;
    const domains = (kyloeCallValue?.domains || []).filter((d: string) => d && d.trim() !== '');

    const kyloeCallConfig: IKyloeCallIntegrationConfig | null =
      domains.length > 0
        ? {
            domains: domains,
            botName: kyloeCallValue?.botName as string,
            active: kyloeCallValue?.active || false,
          }
        : null;

    const eightByEightValue = this.form.value.eightByEight;

    const integrationSave$ = this.noteTakerIntegrationApiService.putIntegrationConfig({
      kyloeCall: kyloeCallConfig,
      ringover: ringoverValues,
    });

    const eightByEightSave$ = this.noteTaker8x8ApiService.putIntegrationConfig({
      apiKey: eightByEightValue.apiKey || null,
      apiSecret: eightByEightValue.apiSecret || null,
      enabled: eightByEightValue.enabled,
    });

    const callOptionsSave$ = this.notetakerCallsService.updateCallOptions(this.corpId, {
      saveTranscriptAsFile: this.form.value.saveTranscriptAsFile,
      oneNotePerCall: this.form.value.oneNotePerCall,
      summaryPrompt: this.form.value.summaryPrompt ?? '',
    });

    this.dialogRef.close();

    forkJoin([integrationSave$, eightByEightSave$, callOptionsSave$]).subscribe({
      next: () => {
        this.messageNotificationService.success('Settings saved successfully');
      },
      error: (error) => {
        this.messageNotificationService.error('Failed to save settings', error);
      },
    });
  }
}
